import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageContainer from '../../../components/Redesing/page-container';
import Card from '@mui/material/Card';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import { history, toSentenceCase } from '../../../utils';
import useDirectusData from '../../../hooks/directus/query/useDirectusData';
import { DirectusData } from '../../../ts/interface/directus';
import useUrlState from '../../../hooks/use-url-state';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';

import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Option, SearchChip } from '../../../ts/interface/components/table.interface';
import {
  MovaticFieldGroupSelectFilter,
  MovaticFieldGroupText,
} from '../../../components/Redesing/movatic-field-group';
import { userColumns } from '../../../tableColumns/userColumns';
import {
  useUsersRelayQuery,
  mapRelayUsersToIUserDetails,
  UserSortInput,
  UserQueryFilter,
} from '../../../hooks/users/query/V2';
import { IUserDetail } from '../../../ts/interface/userDetail.interface';
import { SeverityPill } from '../../../components/Redesing/severity-pill';
import SendIcon from '@mui/icons-material/Send';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import { Typography } from '@mui/material';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import { sendUserNotifications } from '../../../actionCreators/system';
import CustomToast from '../../../components/Redesing/custom-toast';
import useOrganization from '../../../hooks/use-organization';
import OrganizationSystemMultiSelect from '../../../components/system/organization-system-multi-select';
import OrganizationSystemSelect from '../../../components/system/organization-system-select';
import { ISystem } from '../../../ts/interface/system.interface';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import { useKeysetPagination } from '../../../hooks/use-keyset-pagination';

const filterOptions = [{ value: 'non-hidden', label: 'Non-Hidden' }];

const sortFieldMap: Record<string, string> = {
  fullName: 'FULL_NAME',
  joined: 'JOINED',
};

function OrganizationUsersPage() {
  const organizationInfo = useOrganization();

  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  const [filters, setFilters, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    // Pending dropdown selections (not yet applied).
    selectedStatus: [{ label: 'Non-Hidden', value: 'non-hidden' }] as Option[],
    selectedSystem: [] as { value: string; label: string; original: ISystem }[],
    // Applied filters (drive the query + chips).
    status: 'non-hidden',
    system: '',
    sort: {
      key: 'fullName',
      direction: 'asc',
    } as ISortConfig,
  });
  const [exportData, setExportData] = useState<boolean>(false);

  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationDescription, setNotificationDescription] = useState('');
  const [notificationSystem, setNotificationSystem] = useState<ISystem | null>(null);

  const debouncedSearchTerm = useSearchDebounce(filters.search);

  const isEnabled = isComplete && Boolean(organizationInfo?.id);

  // Build GraphQL sort from applied sort state.
  const graphqlSort: UserSortInput[] = useMemo(() => {
    const field = sortFieldMap[filters.sort.key];
    if (!field) return [{ field: 'CREATED_ON' as const, order: 'DESC' as const }];
    return [
      {
        field: field as UserSortInput['field'],
        order: (filters.sort.direction === 'asc' ? 'ASC' : 'DESC') as 'ASC' | 'DESC',
      },
    ];
  }, [filters.sort]);

  // Build server-side GraphQL filter from the applied, URL-persisted values
  // (organization-scoped).
  const userFilter = useMemo((): UserQueryFilter | undefined => {
    if (!organizationInfo?.id) return undefined;
    const filter: UserQueryFilter = { organizationId: organizationInfo.id };
    if (debouncedSearchTerm) {
      // Server-side: matches the name, plus the phone number when the term is
      // all digits (see UserQueryFilter.search).
      filter.search = debouncedSearchTerm;
    }
    if (filters.status === 'non-hidden') {
      // Non-hidden filter is applied server-side via the userSystems.hidden clause.
      filter.hidden = false;
    }
    const systemIds = filters.system.split(',').filter((id: string) => Boolean(id));
    if (systemIds.length > 0) {
      filter.systemIds = systemIds;
    }
    return filter;
  }, [organizationInfo?.id, debouncedSearchTerm, filters.status, filters.system]);

  const {
    data: graphqlData,
    count: totalUsers,
    hasMore: graphqlHasMore,
    endCursor,
    isLoading: isLoadingUsers,
    isRefetching,
  } = useUsersRelayQuery(userFilter, {
    limit: filters.sizePerPage,
    after,
    sort: graphqlSort,
    enabled: isEnabled,
  });

  // Only the current page's rows are shown (server-side pagination).
  const userData = useMemo(
    () => (graphqlData ? mapRelayUsersToIUserDetails(graphqlData) : []),
    [graphqlData]
  );

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphqlData]);

  const { data: directus, isLoading: loadingDirectus } = useDirectusData(
    'adminSiteSpace',
    { code: 'users' },
    true
  ) as { data: { value: DirectusData } | undefined; isLoading: boolean };
  const directusData = directus?.value;

  const columns = useMemo(() => {
    const updatedColumns = [...userColumns];

    const bannedColumn = updatedColumns.find((column) => column.id === 'banned');

    if (!bannedColumn) {
      updatedColumns.push({
        id: 'banned',
        label: 'Status',
        notSortable: true,
        exportFormat: (cell: IUserDetail) => cell.status,
        format: (cell: IUserDetail) => {
          return (
            <SeverityPill color={cell?.status === 'active' ? 'success' : 'error'}>
              {toSentenceCase(cell?.status)}
            </SeverityPill>
          );
        },
      });
    }

    return updatedColumns;
  }, []);

  const [chips, setChips] = useState<SearchChip[]>([]);

  // Rebuild the full chip list from the *applied*, URL-persisted filters that
  // drive the query so the chips always match the fetched data — after a
  // refresh and when a filter was cleared (no chip).
  useEffect(() => {
    if (isRestoring) return;

    const next: SearchChip[] = [];

    if (filters.status) {
      const option = filterOptions.find((o) => o.value === filters.status);
      next.push({
        label: 'Configuration',
        field: 'configuration',
        value: filters.status,
        displayValue: option?.label ?? filters.status,
        disabled: false,
      });
    }

    filters.system
      .split(',')
      .filter((id: string) => Boolean(id))
      .forEach((value: string) => {
        const system = filters.selectedSystem?.find((s) => s.value === value);
        next.push({
          label: 'System',
          field: 'system',
          value,
          displayValue: system?.label ?? value,
        });
      });

    setChips(next);
  }, [isRestoring, filters.status, filters.system, filters.selectedSystem]);

  const pageButtons = [
    {
      label: 'Export',
      onClick: () => setExportData(true),
      startIcon: <FileDownloadIcon />,
    },
    {
      startIcon: <AddIcon />,
      label: 'Add',
      onClick: () => history.push('/users/add'),
    },
    {
      label: 'Add CSV',
      onClick: () => history.push('/users/add_csv'),
      startIcon: <FileUploadIcon />,
    },
    {
      label: 'Send notifications',
      onClick: () => setOpenFilterModal(true),
      startIcon: <SendIcon />,
    },
  ];

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      resetPagination();
      setChips((prevChips) =>
        prevChips.filter((group) => group.label !== deletedGroup.label)
      );

      if (deletedGroup.label === 'Configuration') {
        setFilters((prevState) => ({
          ...prevState,
          status: '',
          selectedStatus: [],
        }));
      }
      if (deletedGroup.label === 'System') {
        setFilters((prevState) => ({
          ...prevState,
          system: '',
          selectedSystem: [],
        }));
      }
    },
    [setChips, setFilters, resetPagination]
  );

  return (
    <PageContainer
      csvReport={{
        filename: 'Users.csv',
        // NOTE: export reflects only the current page's rows (server-side
        // pagination fetches one page at a time).
        data: userData || [],
        headers: [
          'fullName',
          'emailAddress',
          'phone',
          'joined',
          'has_membership',
          'banned',
        ],
      }}
      onImportClick={() => history.push('/users/add_csv')}
      importLabel={'Add CSV'}
      spaceName={'Users'}
      documentationLink={directusData?.documentation_link}
    >
      <Card>
        <PaginationTable
          dataId="user-org"
          chips={chips}
          handleChipDelete={handleChipDelete}
          isLoading={loadingDirectus || isLoadingUsers || !isComplete || isPaginating}
          isLoadingCache={isRefetching}
          onApplyFilter={() => {
            const nextStatus = filters.selectedStatus?.[0]?.value ?? '';
            if (nextStatus !== filters.status) {
              resetPagination();
              setFilters((prevState) => ({
                ...prevState,
                status: nextStatus,
              }));
            }

            const nextSystem = filters.selectedSystem.map((item) => item.value).join(',');
            if (nextSystem !== filters.system) {
              resetPagination();
              setFilters((prevState) => ({
                ...prevState,
                system: nextSystem,
              }));
            }
          }}
          spaceFilters={
            <>
              <MovaticFieldGroupSelectFilter
                label={'Status'}
                value={filters.selectedStatus}
                onChange={(event: React.SyntheticEvent, newValue: any[]) => {
                  setFilters((prevState) => ({
                    ...prevState,
                    selectedStatus: newValue
                      .filter((option) =>
                        filterOptions.some(
                          (item) => item.value === (option.value ?? option)
                        )
                      )
                      .map((option) => {
                        const value = option.value ?? option;
                        return {
                          label: filterOptions.find((item) => item.value === value)!
                            .label,
                          value,
                        };
                      }),
                  }));
                }}
                options={filterOptions}
              />
              <OrganizationSystemMultiSelect
                organization_id={organizationInfo.id}
                selectedSystem={filters.selectedSystem}
                onChange={(systems) => {
                  setFilters((prevState) => ({
                    ...prevState,
                    selectedSystem: systems.map((system) => ({
                      label: system?.name,
                      value: system?.gid,
                      original: system,
                    })),
                  }));
                }}
              />
            </>
          }
          padding={{
            pl: -1,
            pt: 1,
            pr: 1,
          }}
          exportData={exportData}
          csvFileName={'Users.csv'}
          onExport={() => setExportData(false)}
          buttons={pageButtons}
          onSortChange={(sort) => {
            resetPagination();
            setFilters((prevState) => ({
              ...prevState,
              sort: {
                key: sort.key,
                direction: sort.direction as 'desc' | 'asc',
              },
            }));
          }}
          onRowCLick={(id: string) => history.push(`/users/${id}/general`)}
          customKey={'phone'}
          sortBy={filters.sort?.key}
          sortDirection={filters.sort?.direction}
          handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
            resetPagination();
            setFilters((prevState) => ({
              ...prevState,
              search: e.target.value,
            }));
          }}
          searchText={filters.search}
          columns={columns as IColumn[]}
          items={userData || []}
          totalItems={totalUsers}
          hasMore={graphqlHasMore}
          onPageChange={(page: any, pageN: number) => onPageChange(pageN)}
          onRowsPerPageChange={(row: any) => {
            resetPagination();
            setFilters((prevState) => ({
              ...prevState,
              sizePerPage: Number(row.target.value),
            }));
          }}
          page={pageNumber}
          rowsPerPage={filters.sizePerPage}
          searchPlaceholder={'Search users'}
          localSearch={false}
          noDataSlice
        />
      </Card>
      <MovaticCustomModal
        customStyles={{
          title: {
            p: 2,
          },
          action: {
            p: -2,
          },
          content: {
            p: 2,
          },
        }}
        open={openFilterModal}
        onClose={() => setOpenFilterModal(false)}
        content={
          <div>
            <Typography variant="body1">
              This action will send notifications to all users
            </Typography>

            <OrganizationSystemSelect
              organization_id={organizationInfo.id}
              selectedSystem={{
                value: notificationSystem?.gid || '',
                name: notificationSystem?.name || '',
              }}
              onChange={(system: ISystem) => setNotificationSystem(system)}
            />
            <MovaticFieldGroupText
              id="chargePendingDescriptionGroup"
              label="Title (Required)"
              value={notificationTitle || ''}
              onChange={(e) => setNotificationTitle(e.target.value)}
              description="The name of the system will be prepended to this title."
            />
            <MovaticFieldGroupText
              multiline
              rows={2}
              id="chargePendingDescriptionGroup"
              label="Description (Required)"
              value={notificationDescription || ''}
              onChange={(e) => setNotificationDescription(e.target.value)}
            />
          </div>
        }
        title={'Send notifications'}
        action={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => setOpenFilterModal(false),
              },
              {
                label: 'Send',
                onClick: () => {
                  if (!Boolean(notificationSystem?.gid)) {
                    CustomToast({
                      message: 'Please select a system to send notifications to',
                      type: 'error',
                    });
                    return;
                  }

                  CustomAsyncToast({
                    promise: () =>
                      sendUserNotifications(
                        notificationTitle,
                        notificationDescription,
                        notificationSystem?.gid || ''
                      ),
                    successMessage: () => 'The notifications were sent successfully',
                    loadingMessage: 'Sending notifications...',
                    errorMessage: 'There was an error sending the notifications',
                  }).then(() => {
                    setOpenFilterModal(false);
                    setNotificationTitle('');
                    setNotificationDescription('');
                  });
                },
                disabled:
                  !notificationTitle ||
                  !notificationDescription ||
                  !notificationSystem?.gid,
              },
            ]}
          />
        }
      />
    </PageContainer>
  );
}

export default OrganizationUsersPage;
